/* eslint-disable no-undef */
import { SelectorType } from '../BaseAutoTracker';

/**
 * Utilitários para manipulação de seletores CSS e elementos DOM
 */
export class SelectorUtils {
  /**
   * Valida se um seletor é válido
   */
  public static isValidSelector(
    selector: string,
    selectorType: SelectorType
  ): boolean {
    try {
      const fullSelector = this.buildSelector(selector, selectorType);
      document.querySelector(fullSelector);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Constrói seletor CSS completo
   */
  public static buildSelector(
    selector: string,
    selectorType: SelectorType
  ): string {
    switch (selectorType) {
      case 'class':
        return `.${selector}`;
      case 'id':
        return `#${selector}`;
      case 'attribute':
        return `[${selector}]`;
      case 'data-attribute':
        return `[data-${selector}]`;
      case 'tag':
        return selector;
      case 'url':
        return `[href="${selector}"]`;
      case 'custom':
        return selector;
      default:
        return selector;
    }
  }

  /**
   * Encontra elementos com retry automático
   */
  public static async findElementsWithRetry(
    selector: string,
    selectorType: SelectorType,
    maxRetries: number = 3,
    retryDelay: number = 1000
  ): Promise<NodeList> {
    // Tratamento especial para busca por texto
    if (selectorType === 'text') {
      return this.findElementsByText(selector, maxRetries, retryDelay);
    }

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      const builtSelector = this.buildSelector(selector, selectorType);

      const elements = document.querySelectorAll(builtSelector);

      if (elements.length > 0) {
        return elements;
      }

      if (attempt < maxRetries - 1) {
        await new Promise(resolve => setTimeout(resolve, retryDelay));
      }
    }

    return document.querySelectorAll(
      this.buildSelector(selector, selectorType)
    );
  }

  /**
   * Encontra elementos pelo texto usando busca manual (para contornar limitação do :contains())
   */
  private static async findElementsByText(
    searchText: string,
    maxRetries: number = 3,
    retryDelay: number = 1000
  ): Promise<NodeList> {
    const results: Element[] = [];
    const normalizedSearchText = searchText.trim().toLowerCase();

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      // Busca em todos os elementos interativos (botões, links, inputs, etc.)
      const allElements = document.querySelectorAll(
        'button, a, input[type="button"], input[type="submit"], [role="button"], form button, form input[type="submit"]'
      );

      results.length = 0; // Limpa resultados anteriores

      allElements.forEach(element => {
        const elementText = element.textContent?.trim().toLowerCase() || '';
        const elementValue =
          (element as HTMLInputElement).value?.trim().toLowerCase() || '';
        const elementTitle =
          element.getAttribute('title')?.trim().toLowerCase() || '';
        const elementAriaLabel =
          element.getAttribute('aria-label')?.trim().toLowerCase() || '';

        // Verifica se o texto procurado está presente em qualquer um dos campos
        if (
          elementText.includes(normalizedSearchText) ||
          elementValue.includes(normalizedSearchText) ||
          elementTitle.includes(normalizedSearchText) ||
          elementAriaLabel.includes(normalizedSearchText)
        ) {
          results.push(element);
        }
      });

      if (results.length > 0) {
        const nodeListWrapper = Object.create(NodeList.prototype) as any;

        Object.defineProperty(nodeListWrapper, 'length', {
          value: results.length,
          writable: false,
          enumerable: true,
          configurable: false,
        });

        results.forEach((element, index) => {
          Object.defineProperty(nodeListWrapper, index.toString(), {
            value: element,
            writable: false,
            enumerable: true,
            configurable: false,
          });
        });

        nodeListWrapper.item = function (index: number): Element | null {
          return results[index] || null;
        };

        nodeListWrapper.forEach = function (
          callback: (element: Element, index: number, list: NodeList) => void,
          thisArg?: any
        ) {
          results.forEach((element, index) => {
            callback.call(thisArg, element, index, nodeListWrapper);
          });
        };

        nodeListWrapper[Symbol.iterator] = function* () {
          for (let i = 0; i < results.length; i++) {
            yield results[i];
          }
        };

        nodeListWrapper.entries = function* () {
          for (let i = 0; i < results.length; i++) {
            yield [i, results[i]] as [number, Element];
          }
        };

        nodeListWrapper.keys = function* () {
          for (let i = 0; i < results.length; i++) {
            yield i;
          }
        };

        nodeListWrapper.values = function* () {
          for (const element of results) {
            yield element;
          }
        };

        return nodeListWrapper as NodeList;
      }

      if (attempt < maxRetries - 1) {
        await new Promise(resolve => setTimeout(resolve, retryDelay));
      }
    }

    const emptyNodeList: any = Object.create(NodeList.prototype);
    emptyNodeList.length = 0;
    emptyNodeList.item = () => null;
    emptyNodeList.forEach = () => {};
    emptyNodeList[Symbol.iterator] = function* () {};
    return emptyNodeList as NodeList;
  }

  /**
   * Verifica se um elemento está visível
   */
  public static isElementVisible(element: Element): boolean {
    const style = window.getComputedStyle(element);
    const htmlElement = element as HTMLElement;
    return (
      style.display !== 'none' &&
      style.visibility !== 'hidden' &&
      style.opacity !== '0' &&
      htmlElement.offsetWidth > 0 &&
      htmlElement.offsetHeight > 0
    );
  }

  /**
   * Verifica se um elemento está no viewport
   */
  public static isElementInViewport(element: Element): boolean {
    const rect = element.getBoundingClientRect();
    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <=
        (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
  }

  /**
   * Obtém posição do elemento na página
   */
  public static getElementPosition(element: Element): {
    top: number;
    left: number;
    bottom: number;
    right: number;
    width: number;
    height: number;
  } {
    const rect = element.getBoundingClientRect();
    return {
      top: rect.top + window.scrollY,
      left: rect.left + window.scrollX,
      bottom: rect.bottom + window.scrollY,
      right: rect.right + window.scrollX,
      width: rect.width,
      height: rect.height,
    };
  }

  /**
   * Obtém dados do elemento para tracking
   */
  public static extractElementData(element: Element): {
    tagName: string;
    id?: string;
    classes: string[];
    attributes: Record<string, string>;
    textContent: string;
    position: ReturnType<typeof SelectorUtils.getElementPosition>;
    isVisible: boolean;
    isInViewport: boolean;
  } {
    const attributes: Record<string, string> = {};
    Array.from(element.attributes).forEach(attr => {
      attributes[attr.name] = attr.value;
    });

    return {
      tagName: element.tagName.toLowerCase(),
      id: element.id || undefined,
      classes: element.className ? element.className.split(' ') : [],
      attributes,
      textContent: element.textContent?.trim() || '',
      position: this.getElementPosition(element),
      isVisible: this.isElementVisible(element),
      isInViewport: this.isElementInViewport(element),
    };
  }

  /**
   * Cria seletor único para um elemento
   */
  public static createUniqueSelector(element: Element): string {
    if (element.id) {
      return `#${element.id}`;
    }

    const path: string[] = [];
    let current: Element | null = element;

    while (current && current !== document.documentElement) {
      let selector = current.tagName.toLowerCase();

      if (current.className) {
        const classes = current.className.split(' ').filter(c => c.trim());
        if (classes.length > 0) {
          selector += '.' + classes.join('.');
        }
      }

      // Adiciona nth-child se necessário
      const siblings = Array.from(current.parentElement?.children || []);
      const index = siblings.indexOf(current);
      if (siblings.length > 1) {
        selector += `:nth-child(${index + 1})`;
      }

      path.unshift(selector);
      current = current.parentElement;
    }

    return path.join(' > ');
  }

  /**
   * Verifica se um seletor corresponde a um elemento
   */
  public static matchesSelector(element: Element, selector: string): boolean {
    try {
      return element.matches(selector);
    } catch {
      return false;
    }
  }

  /**
   * Obtém todos os elementos que correspondem a um seletor
   */
  public static querySelectorAll(selector: string): NodeListOf<Element> {
    try {
      return document.querySelectorAll(selector);
    } catch (error) {
      console.warn('Seletor inválido:', selector, error);
      return document.querySelectorAll('');
    }
  }

  /**
   * Obtém o primeiro elemento que corresponde a um seletor
   */
  public static querySelector(selector: string): Element | null {
    try {
      return document.querySelector(selector);
    } catch (error) {
      console.warn('Seletor inválido:', selector, error);
      return null;
    }
  }
}
